import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, TrendingUp, User, Calendar, Filter, FileText, Download, Eye, DollarSign } from 'lucide-react';
import ShiftReportDialog from './ShiftReportDialog';
import CashPickupDialog from './CashPickupDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShiftHistory() {
  const [shifts, setShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filters
  const [dateFilter, setDateFilter] = useState('all'); // all, today, this_week, this_month, custom
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [kasirFilter, setKasirFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  
  // Report dialog
  const [selectedShift, setSelectedShift] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showCashPickupDialog, setShowCashPickupDialog] = useState(false);
  
  // Lists for filters
  const [kasirList, setKasirList] = useState([]);
  const [branchList, setBranchList] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    fetchShifts();
    if (isAdmin) {
      fetchKasirList();
      fetchBranchList();
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    // Only apply filters when filter values change, not when pagination changes
    // Because pagination already fetches the correct data from backend
    applyFilters();
  }, [dateFilter, customDateRange, kasirFilter, branchFilter]);

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch shifts with pagination
      const response = await axios.get(`${API_URL}/api/shifts/history`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { 
          limit: itemsPerPage,
          offset: (currentPage - 1) * itemsPerPage
        }
      });
      
      if (response.data.success) {
        const shiftsData = response.data.shifts || [];
        
        // No need to fetch transactions anymore - just use shift data directly
        setShifts(shiftsData);
        setFilteredShifts(shiftsData);
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      toast.error('Gagal memuat riwayat shift');
    } finally {
      setLoading(false);
    }
  };

  const fetchKasirList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        const kasirs = response.data.users.filter(u => u.role === 'kasir');
        setKasirList(kasirs);
      }
    } catch (error) {
      console.error('Error fetching kasir list:', error);
    }
  };

  const fetchBranchList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranchList(response.data.branches || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...shifts];

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate, endDate = now;

      if (dateFilter === 'today') {
        startDate = new Date(now.setHours(0, 0, 0, 0));
      } else if (dateFilter === 'this_week') {
        const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
        startDate = new Date(weekStart.setHours(0, 0, 0, 0));
      } else if (dateFilter === 'this_month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      } else if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
        startDate = new Date(customDateRange.start);
        endDate = new Date(customDateRange.end);
        endDate.setHours(23, 59, 59, 999);
      }

      if (startDate) {
        filtered = filtered.filter(shift => {
          const shiftDate = new Date(shift.start_time);
          return shiftDate >= startDate && shiftDate <= endDate;
        });
      }
    }

    // Kasir filter
    if (kasirFilter !== 'all') {
      filtered = filtered.filter(shift => shift.user_id === kasirFilter);
    }

    // Branch filter
    if (branchFilter !== 'all') {
      filtered = filtered.filter(shift => shift.branch_id === branchFilter);
    }

    setFilteredShifts(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'Aktif';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}j ${minutes}m`;
  };

  const handleViewReport = (shift) => {
    setSelectedShift(shift);
    setShowReportDialog(true);
  };

  const exportShiftData = () => {
    try {
      if (filteredShifts.length === 0) {
        toast.warning('Tidak ada data untuk di-export');
        return;
      }

      // Prepare CSV data
      let csvContent = "RIWAYAT SHIFT\n";
      csvContent += `Tanggal Export: ${new Date().toLocaleString('id-ID')}\n`;
      csvContent += `Total Shift: ${filteredShifts.length}\n\n`;
      
      csvContent += "Kasir,Cabang,Mulai Shift,Selesai Shift,Durasi (jam),Modal Awal,Kas Seharusnya,Kas Dihitung,Selisih,Status\n";
      
      filteredShifts.forEach(shift => {
        const startTime = new Date(shift.start_time).toLocaleString('id-ID');
        const endTime = shift.end_time ? new Date(shift.end_time).toLocaleString('id-ID') : '-';
        const duration = shift.end_time ? ((new Date(shift.end_time) - new Date(shift.start_time)) / (1000 * 60 * 60)).toFixed(1) : '-';
        const status = shift.status === 'active' ? 'Aktif' : 'Selesai';
        const accurateExpectedCash = shift.accurate_expected_cash || shift.expected_cash || 0;
        const cashDifference = (shift.ending_cash || 0) - accurateExpectedCash;
        
        csvContent += `"${shift.full_name}",`;
        csvContent += `"${shift.branch || '-'}",`;
        csvContent += `"${startTime}",`;
        csvContent += `"${endTime}",`;
        csvContent += `${duration},`;
        csvContent += `${shift.starting_cash || 0},`;
        csvContent += `${accurateExpectedCash},`;
        csvContent += `${shift.ending_cash || 0},`;
        csvContent += `${cashDifference},`;
        csvContent += `${status}\n`;
      });
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `shift_history_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Data shift berhasil di-export!', {
        description: `${filteredShifts.length} shift telah di-export ke CSV`,
        duration: 3000
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Gagal export data', {
        description: error.message,
        duration: 4000
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600" />
            Riwayat Shift
          </h2>
          <button
            onClick={exportShiftData}
            disabled={filteredShifts.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            Export Data
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Periode
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Semua</option>
              <option value="today">Hari Ini</option>
              <option value="this_week">Minggu Ini</option>
              <option value="this_month">Bulan Ini</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Kasir Filter (Admin only) */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                Kasir
              </label>
              <select
                value={kasirFilter}
                onChange={(e) => setKasirFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Semua Kasir</option>
                {kasirList.map(kasir => (
                  <option key={kasir.user_id} value={kasir.user_id}>
                    {kasir.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Branch Filter (Admin only) */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="h-4 w-4 inline mr-1" />
                Cabang
              </label>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Semua Cabang</option>
                {branchList.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Custom Date Range */}
        {dateFilter === 'custom' && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            <input
              type="date"
              value={customDateRange.start}
              onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={customDateRange.end}
              onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600">
          Menampilkan {filteredShifts.length} dari {shifts.length} shift
        </div>
      </div>

      {/* Shifts List */}
      {filteredShifts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Tidak ada riwayat shift</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filteredShifts.map((shift) => {
            const accurateExpected = shift.accurate_expected_cash || shift.expected_cash || 0;
            const endingCash = shift.ending_cash || 0;
            const difference = endingCash - accurateExpected;
            const isDifferenceOk = Math.abs(difference) < 1000;
            
            return (
              <div
                key={shift.id}
                className="bg-white border border-gray-200 hover:border-blue-400 rounded-lg p-4 transition-all cursor-pointer hover:shadow-md"
                onClick={() => handleViewReport(shift)}
              >
                {/* Header: Kasir & Status - Always Horizontal */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">{shift.full_name}</h3>
                      <p className="text-xs text-gray-500">@{shift.username}</p>
                    </div>
                  </div>
                  {shift.status === 'active' ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">
                      ● Aktif
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full whitespace-nowrap">
                      Selesai
                    </span>
                  )}
                </div>

                {/* Content - Responsive Layout */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Tanggal & Waktu - Grid on mobile, flex on desktop */}
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 lg:flex lg:gap-6">
                    <div>
                      <p className="text-xs text-gray-500">Tanggal</p>
                      <p className="text-sm font-bold text-gray-900">{new Date(shift.start_time).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Mulai</p>
                      <p className="text-sm font-semibold text-gray-900">{new Date(shift.start_time).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    {shift.end_time && (
                      <>
                        <div>
                          <p className="text-xs text-gray-500">Selesai</p>
                          <p className="text-sm font-semibold text-gray-900">{new Date(shift.end_time).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Durasi</p>
                          <p className="text-sm font-semibold text-blue-600">{formatDuration(shift.start_time, shift.end_time)}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Cash Reconciliation - Full width on mobile, side on desktop */}
                  {shift.status === 'closed' && (
                    <div className="border-t lg:border-t-0 lg:border-l pt-3 lg:pt-0 lg:pl-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-2 font-semibold">Rekonsiliasi Kas</p>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Seharusnya:</span>
                              <span className="font-semibold text-gray-900">{formatCurrency(accurateExpected)}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Dihitung:</span>
                              <span className="font-semibold text-gray-900">{formatCurrency(endingCash)}</span>
                            </div>
                            <div className={`flex justify-between text-xs pt-1 border-t ${isDifferenceOk ? 'text-green-600' : 'text-red-600'}`}>
                              <span className="font-semibold">Selisih:</span>
                              <span className="font-bold">{formatCurrency(difference)}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewReport(shift);
                          }}
                          className="px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition flex items-center gap-1.5 whitespace-nowrap"
                          title="Klik untuk melihat data akurat"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Detail
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedShift(shift);
                            setShowCashPickupDialog(true);
                          }}
                          className="px-3 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition flex items-center gap-1.5 whitespace-nowrap"
                          title="Print bukti pengambilan uang"
                        >
                          <DollarSign className="h-3.5 w-3.5" />
                          Ambil Uang
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Handover Info - Below if exists */}
                {shift.handover_to && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                       Diserahkan ke: <span className="font-semibold text-gray-700">{shift.handover_to_name || shift.handover_to}</span>
                      {shift.handover_notes && <span className="text-gray-600"> - {shift.handover_notes}</span>}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && filteredShifts.length > 0 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
          {/* Items per page */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Tampilkan:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-600">per halaman</span>
          </div>

          {/* Page info */}
          <div className="text-sm text-gray-600">
            Halaman {currentPage}
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ← Prev
            </button>
            
            <div className="flex gap-1">
              {[...Array(Math.min(5, Math.ceil(100 / itemsPerPage)))].map((_, i) => {
                const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (pageNum > Math.ceil(100 / itemsPerPage)) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-md text-sm ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={filteredShifts.length < itemsPerPage}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Shift Report Dialog */}
      <ShiftReportDialog
        open={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        shift={selectedShift}
      />

      {/* Cash Pickup Dialog */}
      <CashPickupDialog
        open={showCashPickupDialog}
        onClose={() => setShowCashPickupDialog(false)}
        shift={selectedShift}
      />
    </div>
  );
}
